import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Screen } from "../components/ui/editorial/Screen.js";
import { Topline } from "../components/ui/editorial/Topline.js";
import { PrimaryButton } from "../components/ui/editorial/PrimaryButton.js";
import { CuratorBubble } from "../components/ui/editorial/CuratorBubble.js";
import { useCurator } from "../useCurator.js";

interface Place {
  id: string;
  num: string;
  name: string;
  kind: "кафе" | "книжный" | "бар" | "парк" | "чайная" | "библиотека";
  description: string;
  district: string;
  address: string;
  metro: string;
  lat: number;
  lng: number;
}

// Подборка Марка — тихие места в центре Москвы, где можно долго сидеть
// и разговаривать. Координаты — реальные, по фасаду здания.
// Позже перенесём в DB (schema: curated_places, seed-moscow.ts), но для MVP
// хватает статики — список проверяется редакцией и редко меняется.
const PLACES: Place[] = [
  {
    id: "p1",
    num: "№ 01",
    name: "Март",
    kind: "кафе",
    description:
      "Маленькое, тихое. Хорошие окна и простой кофе. Можно сидеть долго — не прогоняют.",
    district: "Петровка",
    address: "Петровка, 25с1",
    metro: "м. Чеховская",
    lat: 55.7648,
    lng: 37.6167,
  },
  {
    id: "p2",
    num: "№ 02",
    name: "Фаланстер",
    kind: "книжный",
    description:
      "Книжный с душой. Можно ничего не покупать — разговаривать между полками. Свой запах.",
    district: "Тверская",
    address: "Малый Гнездниковский, 12/27",
    metro: "м. Пушкинская",
    lat: 55.7628,
    lng: 37.6076,
  },
  {
    id: "p3",
    num: "№ 03",
    name: "Чайная высота",
    kind: "чайная",
    description: "Чай по часам, деревянные столы, никто не торопит. Хорошо, когда на улице дождь.",
    district: "Покровка",
    address: "Покровка, 27с1",
    metro: "м. Чистые пруды",
    lat: 55.7613,
    lng: 37.6504,
  },
  {
    id: "p4",
    num: "№ 04",
    name: "Патриаршие пруды",
    kind: "парк",
    description:
      "Если погода — просто круг вокруг пруда. Лавочки в тени, утки, тишина между машинами.",
    district: "Патриаршие",
    address: "Малая Бронная",
    metro: "м. Маяковская",
    lat: 55.7637,
    lng: 37.5917,
  },
  {
    id: "p5",
    num: "№ 05",
    name: "Читалка",
    kind: "кафе",
    description:
      "Кафе с книгами и длинными столами. Можно читать, можно говорить — оба формата живут рядом.",
    district: "Чистые пруды",
    address: "Жуковского, 4",
    metro: "м. Чистые пруды",
    lat: 55.7643,
    lng: 37.6458,
  },
  {
    id: "p6",
    num: "№ 06",
    name: "Нейромаркет",
    kind: "кафе",
    description:
      "Спокойно в любой час. Хорошо первый раз — не пафосно, говорить удобно. Варят крепкий кофе.",
    district: "Кузнецкий мост",
    address: "Большая Лубянка, 17",
    metro: "м. Кузнецкий мост",
    lat: 55.7617,
    lng: 37.6285,
  },
  {
    id: "p7",
    num: "№ 07",
    name: "Сад Эрмитаж",
    kind: "парк",
    description:
      "Маленький парк между переулками. Если тепло — скамейки у эстрады. Если нет — кафе в саду.",
    district: "Каретный ряд",
    address: "Каретный Ряд, 3",
    metro: "м. Чеховская",
    lat: 55.7693,
    lng: 37.6105,
  },
  {
    id: "p8",
    num: "№ 08",
    name: "Ходасевич",
    kind: "книжный",
    description:
      "Книжный и маленькое кафе внутри. Свет из окна, книги о кино и поэзии. Хорошо не спешить.",
    district: "Покровка",
    address: "Покровка, 6",
    metro: "м. Китай-город",
    lat: 55.7589,
    lng: 37.6391,
  },
  {
    id: "p9",
    num: "№ 09",
    name: "Библиотека им. Некрасова",
    kind: "библиотека",
    description:
      "Читальный зал, большие столы, бесплатный wi-fi. Первое свидание в тишине — редкий ход.",
    district: "Бауманская",
    address: "Бауманская, 58/25",
    metro: "м. Бауманская",
    lat: 55.772,
    lng: 37.679,
  },
  {
    id: "p10",
    num: "№ 10",
    name: "Энтузиаст",
    kind: "бар",
    description:
      "Бар в гараже во дворе. Вечером — тёплый свет, музыка тихо. Подают простое и хорошее.",
    district: "Столешников",
    address: "Столешников пер., 7с5",
    metro: "м. Охотный ряд",
    lat: 55.7631,
    lng: 37.6155,
  },
  {
    id: "p11",
    num: "№ 11",
    name: "Нескучный сад",
    kind: "парк",
    description:
      "Для тех, кто любит пройтись, а не сидеть. Ольховая аллея к реке — полчаса и разговор.",
    district: "Ленинский",
    address: "Ленинский пр-т, 30",
    metro: "м. Ленинский проспект",
    lat: 55.717,
    lng: 37.5935,
  },
  {
    id: "p12",
    num: "№ 12",
    name: "Пиросмани",
    kind: "кафе",
    description:
      "Грузинская кухня без шика. Тёплый свет, свечи на столах. Подходит, если вечер обещает длиться.",
    district: "Новодевичий",
    address: "Новодевичий пр-д, 4",
    metro: "м. Спортивная",
    lat: 55.7258,
    lng: 37.5531,
  },
];

type GeoState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok"; lat: number; lng: number }
  | { kind: "denied" }
  | { kind: "unavailable" };

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371; // км
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function formatDistance(km: number): string {
  if (km < 1) {
    const m = Math.round((km * 1000) / 10) * 10; // шаг 10 м
    return `${String(m)} м`;
  }
  if (km < 10) return `${km.toFixed(1)} км`;
  return `${String(Math.round(km))} км`;
}

export function Places(): JSX.Element {
  const navigate = useNavigate();
  const curator = useCurator();
  const params = useParams<{ id: string }>();
  const matchId = params.id ?? "04";

  const [picked, setPicked] = useState<string | null>(null);
  const [geo, setGeo] = useState<GeoState>({ kind: "idle" });

  // Запрашиваем геолокацию один раз при монтировании. В Telegram WebApp
  // браузер покажет системный промпт — это ок, мы объясняем, зачем.
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      setGeo({ kind: "unavailable" });
      return;
    }
    setGeo({ kind: "loading" });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({
          kind: "ok",
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGeo({ kind: "denied" });
        else setGeo({ kind: "unavailable" });
      },
      { enableHighAccuracy: false, timeout: 6000, maximumAge: 5 * 60_000 }
    );
  }, []);

  // Считаем расстояние для каждого места — только если есть координаты.
  const placesWithDistance = PLACES.map((p) => {
    const distanceKm = geo.kind === "ok" ? haversineKm(geo.lat, geo.lng, p.lat, p.lng) : null;
    return { ...p, distanceKm };
  });

  // Если геолокация получена — сортируем от ближнего к дальнему.
  const ordered =
    geo.kind === "ok"
      ? [...placesWithDistance].sort((a, b) => (a.distanceKm ?? 0) - (b.distanceKm ?? 0))
      : placesWithDistance;

  const onSubmit = () => {
    navigate(`/sent?next=${encodeURIComponent("/home")}&ms=10000`);
  };

  const geoNote =
    geo.kind === "loading"
      ? "ищем вас в городе…"
      : geo.kind === "ok"
        ? "Москва · по вашему местоположению"
        : geo.kind === "denied"
          ? "Москва · центр (геолокация выключена)"
          : "Москва · центр";

  return (
    <Screen tone="paper">
      <div className="px-[22px] pt-[48px]">
        <Topline left="COMPANION" right="места · Москва" />
      </div>

      <div className="flex flex-1 flex-col px-[22px] pt-[28px]">
        <button
          type="button"
          onClick={() => {
            navigate(`/match/${matchId}`);
          }}
          className="self-start editorial-meta text-ink/55 hover:text-ink transition-colors"
        >
          ← назад
        </button>

        <h1 className="mt-[20px] font-display font-light leading-[0.98] tracking-[-0.028em] text-ink text-[44px]">
          Где <em className="italic text-velvet">встретиться</em>.
        </h1>

        <div className="mt-[14px] flex items-center gap-[10px] editorial-meta text-ink/55">
          <span
            aria-hidden
            className={`inline-block h-[6px] w-[6px] rounded-full ${
              geo.kind === "ok" ? "bg-accent" : "bg-ink/30"
            }`}
          />
          <span>{geoNote}</span>
        </div>

        <div className="mt-[24px] pr-[18px]">
          <CuratorBubble signature={curator.shortSig}>
            «Вы оба рядом. Я знаю несколько мест — спокойные, без суеты. Выберите то, что ближе{" "}
            <em className="italic text-velvet">вам обоим</em>.»
          </CuratorBubble>
        </div>

        <ul className="mt-[28px]">
          {ordered.map((place, i) => {
            const isPicked = picked === place.id;
            const distanceLabel =
              place.distanceKm != null
                ? formatDistance(place.distanceKm)
                : geo.kind === "loading"
                  ? "…"
                  : place.metro;
            return (
              <li key={place.id}>
                <button
                  type="button"
                  onClick={() => {
                    setPicked(place.id);
                  }}
                  aria-pressed={isPicked}
                  className={`block w-full text-left transition-colors ${
                    i === 0 ? "border-t" : ""
                  } border-b border-ink/12 py-[18px]`}
                >
                  <div className="flex items-baseline justify-between editorial-meta text-ink/45">
                    <span className={isPicked ? "text-accent" : undefined}>{place.num}</span>
                    <span className={place.distanceKm != null ? "text-ink/70" : undefined}>
                      {distanceLabel}
                    </span>
                  </div>

                  <div className="mt-[8px] flex items-baseline gap-[10px]">
                    <h3 className="font-display font-light leading-[1.05] tracking-[-0.02em] text-ink text-[26px]">
                      {place.name}
                    </h3>
                    <span className="font-display italic font-light text-[14px] text-ink/55">
                      — {place.kind}
                    </span>
                  </div>

                  <p className="mt-[6px] font-display italic font-light text-[15px] leading-[1.45] text-ink/75">
                    {place.description}
                  </p>

                  <div className="mt-[10px] flex items-center gap-[10px] editorial-meta text-ink/45">
                    <span>{place.district}</span>
                    <span className="h-[2px] w-[2px] rounded-full bg-ink/35" />
                    <span>{place.metro}</span>
                  </div>

                  {isPicked && (
                    <div className="mt-[12px] flex items-center gap-[8px] editorial-meta text-accent">
                      <span
                        aria-hidden
                        className="inline-block h-[6px] w-[6px] rounded-full bg-accent"
                      />
                      <span>выбрано — {place.address}</span>
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>

        <p className="mt-[22px] font-display italic font-light text-[13px] leading-[1.45] text-ink/50">
          {curator.firstName} передаст ваше предложение. Если вам обоим подойдёт — появится время
          встречи.
        </p>
      </div>

      <div className="px-[22px] pb-[24px] pt-[20px]">
        <PrimaryButton disabled={!picked} onClick={onSubmit}>
          Предложить это место
        </PrimaryButton>
      </div>
    </Screen>
  );
}
